import React, { useState, useEffect } from 'react';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import VoiceInputButton from '../../VoiceInputButton';

const ClientBillingDocuments: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  // Invoice settings
  const [serviceDescription, setServiceDescription] = useState<'professional' | 'appointment'>('professional');
  const [professionalServicesText, setProfessionalServicesText] = useState('Professional Services');
  const [automaticInvoiceCreation, setAutomaticInvoiceCreation] = useState<'daily' | 'monthly' | 'manually'>('manually');
  const [pastDueDays, setPastDueDays] = useState('30');
  const [emailPastDueClients, setEmailPastDueClients] = useState(false);
  const [includeInvoiceLogo, setIncludeInvoiceLogo] = useState(true);
  const [invoiceFooter, setInvoiceFooter] = useState('');

  // Statement settings
  const [statementGenerationDay, setStatementGenerationDay] = useState('10');
  const [includeStatementLogo, setIncludeStatementLogo] = useState(false);
  const [statementFooter, setStatementFooter] = useState('');

  // Superbill settings
  const [superbillGenerationDay, setSuperbillGenerationDay] = useState('10');
  const [includeSuperbillLogo, setIncludeSuperbillLogo] = useState(false);
  const [includeSignatureLine, setIncludeSignatureLine] = useState(false);
  const [includeDiagnosisDescription, setIncludeDiagnosisDescription] = useState(true);
  const [superbillFooter, setSuperbillFooter] = useState('');

  // General billing automations
  const [deliveryDelay, setDeliveryDelay] = useState('15');
  const [createStatementsForNewClients, setCreateStatementsForNewClients] = useState(false);
  const [createSuperbillsForNewClients, setCreateSuperbillsForNewClients] = useState(false);
  const [defaultNotificationMethod, setDefaultNotificationMethod] = useState<'none' | 'email_attached' | 'email_portal'>('none');

  // Load settings from Firestore
  useEffect(() => {
    if (!user) return;
    const loadSettings = async () => {
      try {
        const docRef = doc(db, 'billing_settings', 'client_billing_documents');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.invoice) {
            setServiceDescription(data.invoice.serviceDescription || 'professional');
            setProfessionalServicesText(data.invoice.professionalServicesText || 'Professional Services');
            setAutomaticInvoiceCreation(data.invoice.automaticInvoiceCreation || 'manually');
            setPastDueDays(String(data.invoice.pastDueDays || 30));
            setEmailPastDueClients(data.invoice.emailPastDueClients || false);
            setIncludeInvoiceLogo(data.invoice.includeInvoiceLogo !== false);
            setInvoiceFooter(data.invoice.invoiceFooter || '');
          }
          if (data.statement) {
            setStatementGenerationDay(String(data.statement.statementGenerationDay || 10));
            setIncludeStatementLogo(data.statement.includeStatementLogo || false);
            setStatementFooter(data.statement.statementFooter || '');
          }
          if (data.superbill) {
            setSuperbillGenerationDay(String(data.superbill.superbillGenerationDay || 10));
            setIncludeSuperbillLogo(data.superbill.includeSuperbillLogo || false);
            setIncludeSignatureLine(data.superbill.includeSignatureLine || false);
            setIncludeDiagnosisDescription(data.superbill.includeDiagnosisDescription !== false);
            setSuperbillFooter(data.superbill.superbillFooter || '');
          }
          if (data.generalAutomations) {
            setDeliveryDelay(String(data.generalAutomations.deliveryDelay || 15));
            setCreateStatementsForNewClients(data.generalAutomations.createStatementsForNewClients || false);
            setCreateSuperbillsForNewClients(data.generalAutomations.createSuperbillsForNewClients || false);
            setDefaultNotificationMethod(data.generalAutomations.defaultNotificationMethod || 'none');
          }
        }
      } catch (err) {
        console.error('Error loading client billing documents settings:', err);
      }
    };
    loadSettings();
  }, [user]);

  const handleSaveChanges = async () => {
    const settings = {
      invoice: {
        serviceDescription,
        professionalServicesText,
        automaticInvoiceCreation,
        pastDueDays: parseInt(pastDueDays),
        emailPastDueClients,
        includeInvoiceLogo,
        invoiceFooter
      },
      statement: {
        statementGenerationDay: parseInt(statementGenerationDay),
        includeStatementLogo,
        statementFooter
      },
      superbill: {
        superbillGenerationDay: parseInt(superbillGenerationDay),
        includeSuperbillLogo,
        includeSignatureLine,
        includeDiagnosisDescription,
        superbillFooter
      },
      generalAutomations: {
        deliveryDelay: parseInt(deliveryDelay),
        createStatementsForNewClients,
        createSuperbillsForNewClients,
        defaultNotificationMethod
      },
      ownerId: user?.uid || '',
      updatedAt: new Date().toISOString()
    };

    try {
      const docRef = doc(db, 'billing_settings', 'client_billing_documents');
      await setDoc(docRef, settings, { merge: true });
      toast.success('Client billing documents settings saved successfully!');
    } catch (err) {
      console.error('Error saving client billing documents settings:', err);
      toast.error('Failed to save client billing documents settings.');
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 4px 0' }}>
            Client billing documents
          </h1>
          <p style={{ fontSize: '13px', color: '#87CEEB', margin: 0 }}>
            Automate invoices, statements, superbills
          </p>
        </div>
        <button
          onClick={handleSaveChanges}
          style={{
            backgroundColor: '#1E90FF',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 20px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Save changes
        </button>
      </div>

      {/* Video Link */}
      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <span
          style={{
            color: '#1E90FF',
            fontSize: '13px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span>📺</span> Watch a quick video about Client billing documents
        </span>
      </div>

      {/* Invoices Section */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px', borderBottom: '2px solid #6CA6C1', paddingBottom: '8px' }}>
          Invoices
        </h2>

        {/* Service description */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Service description
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '12px' }}>
            Choose the default text that will be used for line items on your invoices
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={serviceDescription === 'professional'}
              onChange={() => setServiceDescription('professional')}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Use</span>
            <input
              type="text"
              value={professionalServicesText}
              onChange={(e) => setProfessionalServicesText(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px',
                width: '250px'
              }}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={serviceDescription === 'appointment'}
              onChange={() => setServiceDescription('appointment')}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Use Appointment Service and Description</span>
          </label>
        </div>

        {/* Automatic invoice creation */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Automatic invoice creation
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '6px' }}>
            Invoices can be created manually anytime. You can set them to be created automatically on a daily or monthly basis.
          </p>
          <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '12px' }}>
            Note: This setting will not be sending clients invoices automatically. To do this, choose an option in Email Billing Notification in the Billing Documents for New Clients section below. <span style={{ color: '#1E90FF' }}>Learn more.</span>
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={automaticInvoiceCreation === 'daily'}
              onChange={() => setAutomaticInvoiceCreation('daily')}
              style={{ cursor: 'pointer' }}
            />
            <div style={{ fontSize: '14px', color: '#FFFFFF' }}>
              <span style={{ fontWeight: 500 }}>Daily:</span>
              {' '}
              <span style={{ color: '#87CEEB' }}>Automatically create invoices at the end of each day. (Recommended)</span>
            </div>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={automaticInvoiceCreation === 'monthly'}
              onChange={() => setAutomaticInvoiceCreation('monthly')}
              style={{ cursor: 'pointer' }}
            />
            <div style={{ fontSize: '14px', color: '#FFFFFF' }}>
              <span style={{ fontWeight: 500 }}>Monthly:</span>
              {' '}
              <span style={{ color: '#87CEEB' }}>Automatically create invoices at the end of each month.</span>
            </div>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={automaticInvoiceCreation === 'manually'}
              onChange={() => setAutomaticInvoiceCreation('manually')}
              style={{ cursor: 'pointer' }}
            />
            <div style={{ fontSize: '14px', color: '#FFFFFF' }}>
              <span style={{ fontWeight: 500 }}>Manually:</span>
              {' '}
              <span style={{ color: '#87CEEB' }}>Do not automate invoices. (Only recommended for practices with complex billing workflows)</span>
            </div>
          </label>
        </div>

        {/* Past due invoices */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Past due invoices
            <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '14px' }}>ⓘ</span>
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Invoices will be marked as past due</span>
            <select
              value={pastDueDays}
              onChange={(e) => setPastDueDays(e.target.value)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px',
                width: '80px'
              }}
            >
              {[15, 30, 45, 60, 90].map(days => (
                <option key={days} value={days}>{days}</option>
              ))}
            </select>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>days after the issue date.</span>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={emailPastDueClients}
              onChange={(e) => setEmailPastDueClients(e.target.checked)}
              style={{ marginTop: '2px', cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <div>
              <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Email clients when an invoice is past due. Only for 'Client Portal' and 'Email' delivery methods.</span>
              <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '14px', marginLeft: '6px' }}>ⓘ</span>
            </div>
          </label>
        </div>

        {/* Invoice format */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
            Invoice format
          </h3>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={includeInvoiceLogo}
              onChange={(e) => setIncludeInvoiceLogo(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Include practice logo</span>
          </label>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <label style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                Footer information
              </label>
              <VoiceInputButton onResult={text => setInvoiceFooter(prev => prev + ' ' + text)} />
            </div>
            <textarea
              value={invoiceFooter}
              onChange={(e) => setInvoiceFooter(e.target.value)}
              placeholder="Information that will show in the footer of your billing documents goes here. The character limit is 120 characters."
              maxLength={120}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
      </section>

      {/* Statements Section */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px', borderBottom: '2px solid #6CA6C1', paddingBottom: '8px' }}>
          Statements
        </h2>

        {/* Automatic statement generation */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Automatic statement generation
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '12px' }}>
            Choose which date to generate the previous month's statement.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Generate monthly Statements on day</span>
            <select
              value={statementGenerationDay}
              onChange={(e) => setStatementGenerationDay(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px',
                width: '80px'
              }}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>of the following month.</span>
          </div>
        </div>

        {/* Statement format */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
            Statement format
          </h3>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={includeStatementLogo}
              onChange={(e) => setIncludeStatementLogo(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Include practice logo</span>
          </label>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <label style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                Footer information
              </label>
              <VoiceInputButton onResult={text => setStatementFooter(prev => prev + ' ' + text)} />
            </div>
            <textarea
              value={statementFooter}
              onChange={(e) => setStatementFooter(e.target.value)}
              placeholder="Information that will show in the footer of your billing documents goes here. The character limit is 120 characters."
              maxLength={120}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
      </section>

      {/* Superbills Section */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px', borderBottom: '2px solid #6CA6C1', paddingBottom: '8px' }}>
          Superbills
        </h2>

        {/* Automatic superbill generation */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Automatic superbill generation
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '12px' }}>
            Choose on which day of the following month superbills are generated. This gives you time to close out the accounting for a month.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Generate monthly Superbills on day</span>
            <select
              value={superbillGenerationDay}
              onChange={(e) => setSuperbillGenerationDay(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px',
                width: '80px'
              }}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>of the following month.</span>
          </div>
        </div>

        {/* Superbill format */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
            Superbill format
          </h3>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={includeSuperbillLogo}
              onChange={(e) => setIncludeSuperbillLogo(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Include practice logo</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={includeSignatureLine}
              onChange={(e) => setIncludeSignatureLine(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Include signature line</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={includeDiagnosisDescription}
              onChange={(e) => setIncludeDiagnosisDescription(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Include diagnosis description</span>
          </label>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <label style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                Footer information
              </label>
              <VoiceInputButton onResult={text => setSuperbillFooter(prev => prev + ' ' + text)} />
            </div>
            <textarea
              value={superbillFooter}
              onChange={(e) => setSuperbillFooter(e.target.value)}
              placeholder="Information that will show in the footer of your billing documents goes here. The character limit is 120 characters."
              maxLength={120}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
      </section>

      {/* General Billing Automations Section */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px', borderBottom: '2px solid #6CA6C1', paddingBottom: '8px' }}>
          General billing automations
        </h2>

        {/* Billing document delivery delay */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Billing document delivery delay
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '12px' }}>
            When billing documents are created, including invoices, statements and superbills, you can choose a delay for notifications/emails that are being sent out. This way you can edit/review a document before a client gets a notification. You can control which notifications are being sent for each client individually.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>If enabled for a client, send emails for new billing documents</span>
            <select
              value={deliveryDelay}
              onChange={(e) => setDeliveryDelay(e.target.value)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px',
                width: '120px'
              }}
            >
              <option value="5">5 minutes</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">60 minutes</option>
              <option value="90">90 minutes</option>
            </select>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>after the document has been created.</span>
          </div>
        </div>

        {/* Billing documents for new clients */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Billing documents for new clients
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '12px' }}>
            These are the default settings for every new client you create. You can change these settings after the client is created.
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={createStatementsForNewClients}
              onChange={(e) => setCreateStatementsForNewClients(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Create monthly statements for new clients</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={createSuperbillsForNewClients}
              onChange={(e) => setCreateSuperbillsForNewClients(e.target.checked)}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Create monthly superbills for new clients</span>
          </label>
        </div>

        {/* Default notification method */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Default notification method
          </h3>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '12px' }}>
            Automate the process of notifying new clients when billing documents are available.
          </p>

          <select
            value={defaultNotificationMethod}
            onChange={(e) => setDefaultNotificationMethod(e.target.value as any)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '14px',
              width: '100%',
              maxWidth: '400px'
            }}
          >
            <option value="none">None</option>
            <option value="email_attached">Email with documents attached</option>
            <option value="email_portal">Email with link to Client Portal</option>
          </select>
        </div>
      </section>
    </div>
  );
};

export default ClientBillingDocuments;
